/**
 * Helpers for reading answers from the auth endpoints.
 */

/** Seconds to wait before another OTP can be requested, unless the server says otherwise. */
export const DEFAULT_RESEND_SECONDS = 30;

const DEFAULT_OTP_MESSAGE =
    'If this email is registered, a 6-digit code has been sent. It is valid for 5 minutes.';

/**
 * Reads the answer of POST /rfp/auth/forgot-password and POST /rfp/auth/resend-otp.
 * forgot-password answers the same way whether or not the email is registered (so the screen
 * must not reveal which). The body is a message string, and may be an object carrying
 * `resend_after` (seconds before another OTP can be requested).
 *
 * @returns {{ message: string, resendAfter: number }}
 */
export const parseOtpSentResponse = (data) => {
    const isObject = data !== null && typeof data === 'object';

    let message = '';
    if (typeof data === 'string') message = data.trim();
    else if (isObject && typeof data.message === 'string') message = data.message.trim();
    else if (isObject && typeof data.detail === 'string') message = data.detail.trim();

    const seconds = isObject ? Number(data.resend_after) : NaN;

    return {
        message: message || DEFAULT_OTP_MESSAGE,
        resendAfter: Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : DEFAULT_RESEND_SECONDS,
    };
};

/**
 * POST /rfp/auth/verify-otp answers with a reset token (valid 10 minutes) that the "set a new
 * password" step needs. The token is the response body itself (a string); an object carrying it
 * as `reset_token` / `token` is accepted too.
 *
 * The token is a secret: it is never shown or logged, and the message below is fixed text.
 *
 * @returns {{ resetToken: string, message: string }}  resetToken is '' when none was found
 */
export const parseVerifyOtpResponse = (data) => {
    let resetToken = '';
    if (typeof data === 'string') {
        resetToken = data.trim();
    } else if (data !== null && typeof data === 'object') {
        const candidate = [data.reset_token, data.token].find(
            (value) => typeof value === 'string' && value.trim()
        );
        resetToken = candidate ? candidate.trim() : '';
    }
    return { resetToken, message: 'Code verified. Please set a new password.' };
};

/**
 * Seconds left before another OTP may be requested, from a 429 answer of resend-otp
 * ("Asked again within 30 seconds. The body has retry_after (seconds left)").
 * Falls back to the Retry-After header, then to the default wait.
 */
export const getRetryAfterSeconds = (error) => {
    const data = error?.response?.data;
    const candidates = [
        data?.retry_after,
        data?.detail?.retry_after,
        error?.response?.headers?.['retry-after'],
    ];
    for (const candidate of candidates) {
        const seconds = Number(candidate);
        if (Number.isFinite(seconds) && seconds > 0) return Math.ceil(seconds);
    }
    return DEFAULT_RESEND_SECONDS;
};

/** A readable message for a failed request (validation list, plain detail, network down...). */
export const getApiErrorMessage = (error, fallback) => {
    if (!error?.response) return 'Cannot reach the server. Please check your connection and try again.';

    const { status, data } = error.response;
    const detail = data?.detail;

    if (Array.isArray(detail)) return detail[0]?.msg || fallback;
    if (typeof detail === 'string' && detail) return detail;
    if (typeof data?.message === 'string' && data.message) return data.message;
    if (status === 429) return 'Too many attempts. Please wait a moment and try again.';
    return fallback;
};
